import storageService from "@/services/middleware/storageService";
import { LOCALE_EN_GB, USERS_LOCALE } from "@/utilities/locale";

export const isoDateFormat = /^\d{4}-\d{2}-\d{2}$/; // "YYYY-MM-DD"
export const isoYearMonthFormat = /^\d{4}-(0[1-9]|1[0-2])$/; // "YYYY-MM"
export const isoDateTimeFormat = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/; // "YYYY-MM-DD HH:mm:ss"
export const isoUtcDateTimeFormat =
  /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])T([01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{3}Z)?$/; // "YYYY-MM-DDTHH:mm:ss" or "YYYY-MM-DDTHH:mm:ss.999Z"
export const OPT_INTELLIGENCE_DATE = "intelligence-date";
export const OPT_PERFORMANCE_DATE = "performance-date";
export const OPT_GOOGLE_DATE = "google-date";
export const OPT_GOOGLE_PMAX_DATE = "google-pmax-date";
export const OPT_META_DATE = "meta-date";
export const OPT_MICROSOFT_DATE = "microsoft-date";
export const OPT_TIKTOK_DATE = "tiktok-date";
export const OPT_TABOOLA_DATE = "taboola-date";
export const OPT_REDDIT_DATE = "reddit-date";
export const OPT_HUBSPOT_DATE = "hubspot-date";
export const OPT_LEADS_DATE = "leads-date";

// Date utils

// Add a 0 to zero-pad numbers ("5" → "05")
export const pad2 = (n: number) => String(n).padStart(2, "0");

// Given a Date `base` and an offset in days, returns a new Date shifted by that many days
export const offsetDate = (base: Date, offsetDays: number) => {
  const d = new Date(base);
  d.setDate(d.getDate() + offsetDays);

  return d;
};

export const sameDateLastYear = (input: Date): Date => {
  const d = new Date(input);
  d.setFullYear(d.getFullYear() - 1, d.getMonth() + 1);
  return d;
};

export const sameDateNextYear = (input: Date): Date => {
  const d = new Date(input);
  d.setFullYear(d.getFullYear() + 1, d.getMonth() - 1);
  return d;
};

export const isDateValid = (s: string) => {
  const d = new Date(s);
  return !isNaN(d.getTime());
};

export const toISO = (s: string) => {
  return s.replace(" ", "T");
};

/**
 * Parse anything the backend hands us into a Date in the VIEWER'S timezone,
 * or `null` if it is missing/unparseable.
 *
 * Two traps this exists to close, both of which `new Date(value)` walks into:
 *
 * 1. A date-only string is parsed as UTC midnight per spec, so `new Date`
 *    ("2026-07-10") is 9 Jul for anyone west of Greenwich. Appending a time
 *    forces local-midnight parsing and keeps the calendar day intact.
 * 2. "YYYY-MM-DD HH:mm:ss" (the shape our API returns) is not valid ISO-8601 —
 *    V8 tolerates the space, Safari returns Invalid Date. `toISO` fixes it.
 */
export const toLocalDate = (value: Date | string | number): Date | null => {
  if (value == null || value === "") return null;

  let parsed: Date;

  if (value instanceof Date) {
    parsed = value;
  } else if (typeof value === "number") {
    parsed = new Date(value);
  } else {
    const normalised = isoDateFormat.test(value)
      ? `${value}T00:00:00`
      : toISO(value);
    parsed = new Date(normalised);
  }

  return isNaN(parsed.getTime()) ? null : parsed;
};

export const diffInHours = (startStr: string, endStr: string) => {
  const msInHour = 1000 * 60 * 60;
  const start = new Date(toISO(startStr));
  const end = new Date(toISO(endStr));

  return Math.floor((end.getTime() - start.getTime()) / msInHour);
};

export const diffInDays = (end: Date, start: Date) => {
  const MS_IN_DAY = 1000 * 60 * 60 * 24;
  const diffMs = end.getTime() - start.getTime();
  // Moment truncates toward zero: floor for positives, ceil for negatives
  return diffMs >= 0
    ? Math.floor(diffMs / MS_IN_DAY)
    : Math.ceil(diffMs / MS_IN_DAY);
};

export const getISOWeek = (date: Date) => {
  // 1) Copy date as UTC, but only Y/M/D
  new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  // 2) ISO week day: Mon=1 … Sun=7
  const dayNum = ((date.getUTCDay() + 6) % 7) + 1;
  // 3) Shift date to Thursday of the current ISO week:
  //    d + (4 - dayNum) days
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  // 4) Find Jan 1 of this ISO‐week’s year (Thursday’s year)
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  // 5) Calculate full weeks to that Thursday
  const weekNo = Math.ceil(
    ((date.getTime() - yearStart.getTime()) / 86400000 + // ms → days
      1) /
      7
  );
  return weekNo;
};

export const subtractDays = (date: Date, days: number) => {
  const d = new Date(date.getTime());
  d.setDate(d.getDate() - days);
  return d;
};

export const startOfMonth = (date: Date) => {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    1, // first day
    0,
    0,
    0,
    0
  );
};

export const sendDateRange = (
  dateStart: string,
  dateEnd: string,
  rangeText: string
) => {
  return {
    dateStart: dateStart,
    dateEnd: dateEnd,
    rangeText: rangeText,
  };
};

// Date Formatting functions

type TimeMode = "full" | "start" | "end" | "none";

/**
 * Format a Date as "YYYY-MM-DD[ HH:mm:ss]"
 *
 * @param date   the Date to format
 * @param mode   'full'  → "HH:mm:ss"
 *               'start' → "00:00:00"
 *               'end'   → "23:59:59"
 *               'none'  → (no time part)
 */
export function formatYYYYMMDD(date: Date, mode: TimeMode = "none"): string {
  const YYYY = date.getFullYear();
  const MM = pad2(date.getMonth() + 1);
  const DD = pad2(date.getDate());
  const datePart = `${YYYY}-${MM}-${DD}`;

  let timePart: string;

  switch (mode) {
    case "full":
      const hh = pad2(date.getHours());
      const mm = pad2(date.getMinutes());
      const ss = pad2(date.getSeconds());
      timePart = `${hh}:${mm}:${ss}`;
      break;
    case "start":
      timePart = "00:00:00";
      break;
    case "end":
      timePart = "23:59:59";
      break;
    default:
      timePart = "";
  }

  return timePart ? `${datePart} ${timePart}` : datePart;
}

export const formatDDMMYYYY = (date: Date) => {
  // Input: Date
  // Output: "DD-MM-YYYY"
  const YYYY = date.getFullYear();
  const MM = pad2(date.getMonth() + 1);
  const DD = pad2(date.getDate());

  return `${DD}-${MM}-${YYYY}`;
};

export const formatStringDate = (dateStr: string) => {
  // Input: YYYY-MM-DDTHH:MM:SS.000Z
  // Output: YYYY-MM-DD HH:MM:SS
  if (!dateStr) return "";
  const stringDate = dateStr.replace("T", " ").replace(/\.\d+Z$/, "");

  return stringDate;
};

export const getDateFromString = (dateStr: string) => {
  // Input: YYYY-MM-DDTHH:MM:SS[...]
  // Output: YYYY-MM-DD
  return dateStr?.slice(0, 10);
};

export const formatDateTimeString = (d: string, isStart: boolean) => {
  const hh = isStart ? "00" : "23";
  const mm = isStart ? "00" : "59";
  const ss = isStart ? "00" : "59";
  return `${d} ${hh}:${mm}:${ss}`;
};

// WORD-MONTH FORMATS — ALWAYS ENGLISH, NEVER LOCALISED
// A month name is UI copy, not data: the app is English-only, so "Jul" must stay
// "Jul" and not become "jul" (es), "Juli" (de) or "7月" (ja) in an otherwise
// English screen. LOCALE_EN_GB is just the vehicle for the shape we want —
// day number then short month, "13 Jul 2026".
//
// Nor is the ORDER localised. Localising a numeric date is worth it because
// "09/07" is genuinely ambiguous; "13 Jul 2026" is not ambiguous to anyone, so
// varying it by locale would buy nothing and would let one screen show both
// "13 Jul" (date-picker, KPI heroes) and "Jul 13" (tables) to the same US user.

export const formatDateDayMonthShort = (date: Date) => {
  // Output: 13 Jul — only for the start of a same-year range, where the trailing
  // date already carries the year. Use the WithYear variant for a standalone date.
  return date.toLocaleDateString(LOCALE_EN_GB, {
    day: "numeric",
    month: "short",
  });
};

export const formatDateMonthShortWithYear = (date: Date) => {
  // Output: Jul 2026 — a whole month, no day. For the "month" dimension.
  return date.toLocaleDateString(LOCALE_EN_GB, {
    month: "short",
    year: "numeric",
  });
};

/**
 * Render an hour-of-day bucket ("18") as a clock time ("18:00").
 *
 * This is a plain integer 0–23 from the API, NOT an instant — it must never be
 * fed through Date/toLocalDate, which would read "18" as a year. 24h, like every
 * other time in the app. Returns null when the value is not an hour.
 */
export const formatHour = (value: string | number) => {
  const hour = Number(value);

  if (!Number.isInteger(hour) || hour < 0 || hour > 23) return null;

  return `${pad2(hour)}:00`;
};

export const formatDateDayMonthShortWithYear = (date: Date) => {
  // Output: 13 Jul 2026
  return date.toLocaleDateString(LOCALE_EN_GB, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export interface DateRangeParts {
  start: string;
  end: string;
}

/**
 * The two ends of a date range in word form, with the year shown ONCE — on the
 * trailing (end) date — when both fall in the same year ("30 Jun" + "6 Jul 2026"),
 * and on both ends when they straddle a year boundary ("30 Dec 2025" + "5 Jan 2026").
 *
 * Returned as separate parts (not joined) so a caller can lay them out however it
 * needs — inline with a separator (the KPI hero) or stacked (the Trends card). The
 * shared-year rule lives here so both stay in sync. Empty parts if a date is null.
 */
export const formatDateRangeParts = (
  startDate: Date | null,
  endDate: Date | null
): DateRangeParts => {
  if (!startDate || !endDate) return { start: "", end: "" };

  const sameYear = startDate.getFullYear() === endDate.getFullYear();

  return {
    start: sameYear
      ? formatDateDayMonthShort(startDate)
      : formatDateDayMonthShortWithYear(startDate),
    end: formatDateDayMonthShortWithYear(endDate),
  };
};

// NUMERIC FORMATS — FOLLOW THE USER'S LOCALE
// No language is involved here, only field order and separators, so honouring
// the browser locale is a real win: a US user reads 07/13/2026, a Spanish user
// 13/07/2026, and neither has to guess which number is the month. Canada is the
// odd one out and reads year-first, 2026-07-13.

// Time is deliberately 24h for EVERY locale, including the ones that default to
// 12h (en-US, en-CA). These timestamps live in dense table columns, and
// "10:00:00 PM" / "9:00:00 AM" / "22:00:00" cannot share a fixed width. Handing
// the hour cycle to Intl would also leak locale-specific markers — en-US gives
// "PM" but en-CA and es-MX give "p.m.".
//
// `locale` defaults to the detected browser locale and should be left alone in the
// app; it exists so the Locale settings preview can exercise these very formatters
// under other locales, rather than duplicating Intl calls that would drift.
export const formatTime = (date: Date, locale: string = USERS_LOCALE) => {
  // Output: 22:00:00 — never "10:00:00 PM"
  return date.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

export const formatDate2Digit = (date: Date, locale: string = USERS_LOCALE) => {
  // Output (en-US): 12/31/2025 — (es-ES): 31/12/2025
  return date.toLocaleDateString(locale, {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

export const formatDate2DigitWithTime = (date: Date) => {
  // Output (en-US): 12/31/2025, 10:00:00 — comma matches the word-with-time format
  // and how Intl itself joins a date and time.
  return formatDate2Digit(date) + ", " + formatTime(date);
};

// {"dateStart":"2025-04-28T22:00:00.000Z","dateEnd":"2025-05-12T21:59:59.999Z","rangeText":"Last 14 days"}
export const detectFormat = (key: string) => {
  const detected = storageService.getItem(key) || {};

  if (
    isoUtcDateTimeFormat.test(detected.dateStart) ||
    isoUtcDateTimeFormat.test(detected.dateEnd)
  ) {
    // Return here the formated date
    return {
      dateStart: formatYYYYMMDD(new Date(detected.dateStart), "start"),
      dateEnd: formatYYYYMMDD(new Date(detected.dateEnd), "end"),
    };
  }

  return detected;
};
